/**
 * __DAY_GRAPH__ (2026-10-20) the Day graph's drawing rules (pure). The hub decides where each job sits
 * (GET /api/board/day-graph: minutes from the day's Toronto midnight -- actual start once started, else the planned
 * start; width = the projected duration; complete = actual start -> finish); this only turns minutes into pixels,
 * hour columns, sub-row tops, the needle and the overrun part. The Dispatch app's src/services/dayGraph.ts is its twin
 * (sim/daygraph.test.mjs checks the same cases on both).
 * __TRUE_DURATION__ (2026-10-28) a bar starts at the job's first stop and is as long as its own estimate (the drive between
 * its own stops + the time at each; the drive to it is not counted); end_kind 'estimated' ends get a faded right end. A
 * route / combo block (b.segments) is one bar with a part per job: segs[] = each part's x / w / colour, a divider line
 * before every part after the first; its overrun is the hub's (the running job's part, from over_from).
 */
export const STARTED = ['en_route', 'at_pickup', 'loading', 'in_transit', 'at_delivery', 'unloading', 'pending_completion'];
export const STATUS_COLORS = {
  pending: '#8b949e', assigned: '#58a6ff', en_route: '#d29922', at_pickup: '#e3742f', loading: '#e3742f',
  in_transit: '#a371f7', at_delivery: '#2ea8a8', unloading: '#2ea8a8', pending_completion: '#39c5cf',
  complete: '#3fb950', completed: '#3fb950', ready_to_bill: '#3fb950', billed: '#6e7681', archived: '#6e7681',
};
export const statusColor = (s) => STATUS_COLORS[s] || '#8b949e';
// __DONE_TODAY__ (2026-10-20) a job done that day: solid green with a ✓ (hauling or Trello complete); late (not started,
// its planned time past, so it waits at the needle): an amber left edge
export const DONE_COLOR = '#2ea043';
export const LATE_COLOR = '#d29922';
export const TIMES_TEXT = { app: 'Times from the driver\'s app', trello: 'Times from Trello (In Progress → Complete)', finish: 'Finish time less the projected length', plan: 'Times from the plan' };
export const LANE_H = 40;
export const ROW_PAD = 5;
export const MIN_BAR = 14;

// __DAY_GRAPH_ZOOM__ (2026-10-20) Henry: "Also add a zoom in and out." px a minute, from the whole day on one screen to
// 12 px (15 minutes = 180 px); the labels / ticks thin out as it zooms out (5 / 15 / 30 / 60 / 120-minute labels).
export const ZOOM_MIN = 0.2;
export const ZOOM_MAX = 12;
export const ZOOM_LEVELS = [0.25, 0.35, 0.5, 0.75, 1, 1.5, 2, 3, 4, 6, 8, 12];
export const clampZoom = (p) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Number(p) || 1));
/** The zoom that fits the day's columns into viewW px. */
export const fitZoom = (viewW, win) => clampZoom(Math.max(60, viewW) / Math.max(60, win.end - win.start));
/** The next level in (+1) or out (-1). */
export const zoomStep = (p, dir) => (dir > 0
  ? (ZOOM_LEVELS.find((l) => l > p * 1.01) || ZOOM_MAX)
  : ([...ZOOM_LEVELS].reverse().find((l) => l < p * 0.99) || ZOOM_MIN));
/** Label every N minutes (at least 56 px apart), ticks between (the finest at least 12 px apart, dividing the label step). */
export const tickPlan = (ppm) => {
  const steps = [5, 15, 30, 60, 120, 180];
  const label = steps.find((s) => s * ppm >= 56) || 180;
  const tick = steps.find((s) => s < label && s * ppm >= 12 && label % s === 0) || null;
  return { label, tick };
};
/** The minute under a screen offset sx (px from the timeline's left edge in view), and the scroll that keeps it there. */
export const minuteAt = (win, scrollX, sx, ppm) => win.start + (scrollX + sx) / ppm;
export const anchorScroll = (win, minute, sx, ppm) => Math.max(0, (minute - win.start) * ppm - sx);
/** The remembered zoom: 'fit' or a number. */
export const readZoom = (v) => {
  const n = Number(v);
  return v && v !== 'fit' && Number.isFinite(n) && n > 0 ? clampZoom(n) : 'fit';
};

/** 'YYYY-MM-DD' of the day in Toronto (the hub's day), not the browser's zone. */
export function torontoDay(d = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Toronto', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  } catch (e) {
    return d.toISOString().slice(0, 10);
  }
}

export function shiftDay(day, n) {
  const [y, m, dd] = String(day).split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, dd + n));
  return t.toISOString().slice(0, 10);
}

/** 435 -> '7:15 AM', 420 -> '7 AM' */
export function clock(min, withMinutes = true) {
  const m = Math.round(Number(min) || 0);
  const h = Math.floor(m / 60) % 24;
  const mm = ((m % 60) + 60) % 60;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const ap = h < 12 ? 'AM' : 'PM';
  return mm && withMinutes ? h12 + ':' + String(mm).padStart(2, '0') + ' ' + ap : h12 + ' ' + ap;
}

/** The needle now: the hub's now_min, moved on by the time since it was fetched. null on another day. */
export function needleMin(graph, fetchedAt, now = Date.now()) {
  if (!graph || graph.now_min == null) return null;
  return graph.now_min + (now - fetchedAt) / 60000;
}

/** graph -> pixels. ppm = pixels per minute. */
export function layout(graph, ppm, needle) {
  const w = (graph && graph.window) || { start: 300, end: 1320 };
  const clampX = (m) => (Math.max(w.start, Math.min(w.end, m)) - w.start) * ppm;
  const width = Math.max(60, (w.end - w.start) * ppm);
  const hours = [];
  const labels = [];
  const ticks = [];
  for (let m = Math.ceil(w.start / 60) * 60; m <= w.end; m += 60) hours.push({ min: m, x: clampX(m), label: clock(m, false) });
  const tp = tickPlan(ppm);   // __DAY_GRAPH_ZOOM__
  for (let m = Math.ceil(w.start / tp.label) * tp.label; m <= w.end; m += tp.label) labels.push({ min: m, x: clampX(m), label: clock(m) });
  if (tp.tick) for (let m = Math.ceil(w.start / tp.tick) * tp.tick; m <= w.end; m += tp.tick) { if (m % tp.label) ticks.push({ min: m, x: clampX(m) }); }
  const rows = ((graph && graph.rows) || []).map((r) => {
    const lanesN = Math.max(1, r.lanes || 1);
    const bars = (r.bars || []).map((b) => {
      const started = STARTED.includes(b.status) && !b.done;
      const segd = !!(b.segments && b.segments.length);   // __TRUE_DURATION__ a route / combo block
      const over = segd ? (!!b.overrun && needle != null && !b.done) : (started && needle != null && needle > b.start + (b.projected_min || 0));
      const end = over ? Math.max(b.end, needle) : b.end;
      const x = clampX(b.start);
      const right = clampX(end);
      const bw = Math.max(MIN_BAR, right - x);
      const n = segd ? b.segments.length : 0;
      const segs = segd ? b.segments.map((sg, i) => {
        const sx = Math.max(0, Math.min(bw, clampX(sg.start) - x));
        const ex = i === n - 1 ? bw : Math.max(sx, Math.min(bw, clampX(sg.end) - x));
        const sw = ex - sx;
        return { ...sg, x: sx, w: sw, divider: i > 0, color: sg.done ? DONE_COLOR : statusColor(sg.status), text: sw >= 70 ? 'full' : sw >= 26 ? 'name' : 'none' };
      }) : null;
      const overAt = segd ? (b.over_from != null ? b.over_from : b.end) : b.start + (b.projected_min || 0);
      return {
        ...b, x, w: bw, text: bw >= 70 ? 'full' : bw >= 26 ? 'name' : 'none', top: ROW_PAD + (b.lane || 0) * LANE_H, h: LANE_H - 6,
        color: b.done ? DONE_COLOR : statusColor(b.status), late: !!b.late && !b.done, edge: b.done ? DONE_COLOR : (b.late ? LATE_COLOR : null),
        overrun: segd ? over || !!b.overrun : !!(b.overrun || over),
        overX: (segd ? (over || b.overrun) : (b.overrun || over)) ? Math.max(0, Math.min(bw, clampX(overAt) - x)) : null,
        segs, estEnd: !b.done && b.end_kind === 'estimated' && !(b.overrun || over),
        ghostBox: b.ghost ? { x: clampX(b.ghost.start), w: Math.max(MIN_BAR, clampX(b.ghost.end) - clampX(b.ghost.start)), top: ROW_PAD + (b.lane || 0) * LANE_H } : null,
      };
    });
    return { ...r, height: lanesN * LANE_H + ROW_PAD * 2, bars };
  });
  const needleX = needle != null && needle >= w.start && needle <= w.end ? clampX(needle) : null;
  return { width, hours, labels, ticks, rows, needleX, window: w, ppm };
}

/** __TRUE_DURATION__ what a job's estimate is made of ('' when the hub had no own estimate). */
export function lengthText(b) {
  if (b.drive_min == null || b.dwell_min == null) return '';
  return 'Estimate: ' + b.drive_min + ' min drive between its stops + ' + b.dwell_min + ' min at its stops (the drive to it is not counted)';
}
/** __TRUE_DURATION__ one job of a route / combo block: its part of the block. */
export function segmentText(sg, i) {
  return (i + 1) + '. ' + (sg.done ? '✓ ' : '') + (sg.customer || '') + ' — ' + (sg.from || '') + (sg.to ? ' → ' + sg.to : '') + ' · '
    + clock(sg.start) + ' – ' + clock(sg.end) + (sg.end_kind === 'actual' ? '' : ' (est.)')
    + (sg.drive_in ? ' · incl. ' + sg.drive_in + ' min drive from the job before' : '');
}

/** The bar's hover / long-press text. */
export function barText(b) {
  const route = (b.from || '') + (b.to ? ' → ' + b.to : '');
  const when = clock(b.start) + ' – ' + clock(b.end) + (b.end_kind === 'estimated' && !b.done ? ' (est. end)' : '');
  const lines = [b.customer || '', route, (b.status_label || b.status || '') + ' · ' + when + (b.projected_min ? ' · projected ' + b.projected_min + ' min' : '')];
  if (lengthText(b)) lines.push(lengthText(b));
  if (b.ghost) lines.push('Planned ' + clock(b.ghost.start) + ' – ' + clock(b.ghost.end));
  if (b.overrun) lines.push('Running past its projected time');
  if (b.done && TIMES_TEXT[b.times]) lines.push('✓ Done · ' + TIMES_TEXT[b.times]);
  if (b.late && !b.done) lines.push('Late: not started and its planned time has passed');
  if (b.truck) lines.push('Truck required: ' + b.truck);   // __OPEN_DECK__ the hub's bar.truck ('' = Any truck: no line)
  if (b.load_number) lines.push('Load ' + b.load_number);
  if (b.segments && b.segments.length) b.segments.forEach((sg, i) => lines.push(segmentText(sg, i)));
  else if (b.members) b.members.forEach((m) => lines.push('• ' + (m.customer || '') + ' — ' + (m.from || '') + (m.to ? ' → ' + m.to : '') + (m.truck ? ' · ' + m.truck : '')));
  return lines.filter(Boolean).join('\n');
}
